import { z } from 'zod';
import { SourceRef, Cents, Biome } from './common.js';

export const Species = z.object({
  id: z.string().regex(/^[a-z][a-z0-9_]*$/),
  common_name: z.string().min(1),
  scientific_name: z.string().min(1),
  class: z.enum(['hoofstock', 'flightless_bird']),
  iucn_status: z.enum(['LC', 'NT', 'VU', 'EN', 'CR', 'EW', 'EX']),
  body: z.object({
    template: z.enum(['deer', 'antelope', 'equid', 'ratite', 'bovid', 'suine']),
    scale: z.number().positive().max(3),
    horn_variant: z.string(),
    adult_weight_kg: z.object({ male: z.number().positive(), female: z.number().positive() }),
  }),
  appearance: z.object({
    default_coat: z.string(),
    morphs: z.array(z.string()).min(1),
  }),
  biology: z.object({
    lifespan_years: z.object({ mean: z.number().positive(), max_observed: z.number().positive() }),
    gestation_days: z.number().int().positive().optional(),
    incubation_days: z.number().int().positive().optional(),
    litter: z.object({ min: z.number().int().positive(), max: z.number().int().positive() }),
    maturity_months: z.number().int().positive(),
    breeding: z.enum(['year_round', 'seasonal']),
  }),
  behavior: z.object({
    forage: z.enum(['grazer', 'browser', 'mixed', 'omnivore']),
    social: z.object({
      type: z.enum(['herd', 'harem', 'flock', 'pair', 'solitary']),
      min_group: z.number().int().positive(),
      male_ratio: z.number().min(0).max(1),
    }),
    activity: z.enum(['diurnal', 'nocturnal', 'crepuscular', 'cathemeral']),
  }),
  habitat: z.object({
    biomes: z.array(Biome).min(1),
    temperature_c: z.object({
      min: z.number(), max: z.number(),
      comfort: z.tuple([z.number(), z.number()]),
    }),
    space_ha_per_animal: z.number().positive(),
    min_fence_strength: z.number().int().min(1).max(3),
    water_l_per_day: z.number().nonnegative(),
  }),
  diet: z.object({
    feed_ids: z.array(z.string()).min(1),
    dmi_pct_bodyweight: z.number().positive().max(10),
    feed_cost_month_cents: Cents.refine((v) => v > 0, 'feed cost must be present for every starter species'),
    feed_cost_basis: z.string().min(1),
  }),
  economics: z.object({
    base_price_cents: Cents.refine((v) => v > 0, 'base price required'),
    breeding_pair_cents: Cents.refine((v) => v > 0, 'breeding pair price required'),
    price_basis: z.string().min(1),
  }),
  traits: z.array(z.string()),
  field: z.object({
    encounters: z.array(z.object({
      biome: Biome,
      rarity: z.enum(['common', 'uncommon', 'rare', 'very_rare']),
    })).min(1),
    sources: z.object({ field_rarity: z.array(z.string()).min(1) }),
  }).optional(),
  sources: z.record(z.string(), z.array(z.string())),
  references: z.array(SourceRef).min(1),
  confidence: z.literal('sourced'),
  last_checked: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  design_defaults: z.array(z.string()).optional(),
  /** Per-fact confidence: high = specific fetched figure, medium = derived/midpoint/single sale, low = design default. */
  field_confidence: z.record(z.string(), z.enum(['high', 'medium', 'low'])).optional(),
}).superRefine((s, ctx) => {
  if (s.class === 'flightless_bird' && s.biology.incubation_days === undefined) {
    ctx.addIssue({ code: 'custom', message: 'flightless_bird requires incubation_days', path: ['biology'] });
  }
  if (s.class === 'hoofstock' && s.biology.gestation_days === undefined) {
    ctx.addIssue({ code: 'custom', message: 'hoofstock requires gestation_days', path: ['biology'] });
  }
  if (s.biology.litter.max < s.biology.litter.min) {
    ctx.addIssue({ code: 'custom', message: 'litter.max < litter.min', path: ['biology', 'litter'] });
  }
});
export type Species = z.infer<typeof Species>;
